import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export default function Popover({
  trigger,
  children,
  placement = "bottom-end",
  className = "",
  offset = 8,
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);
  const [coords, setCoords] = useState({ top: 0, left: 0 });

  // 🔒 Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        !open ||
        popoverRef.current?.contains(e.target) ||
        triggerRef.current?.contains(e.target)
      ) {
        return;
      }
      setOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  // 📍 Position + auto flip if near bottom
  useEffect(() => {
    if (!open) return;

    // Wait for popover to render before measuring
    requestAnimationFrame(() => {
      if (!triggerRef.current || !popoverRef.current) return;

      const triggerRect = triggerRef.current.getBoundingClientRect();
      const popoverRect = popoverRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;

      let top = 0;
      let left = 0;

      const openBottom = () => {
        top = triggerRect.bottom + offset;
        left = placement.includes("end")
          ? triggerRect.right - popoverRect.width
          : triggerRect.left;
      };

      const openTop = () => {
        top = triggerRect.top - popoverRect.height - offset;
        left = placement.includes("end")
          ? triggerRect.right - popoverRect.width
          : triggerRect.left;
      };

      // Try bottom first
      openBottom();

      // 🔁 Auto flip if overflowing bottom
      if (top + popoverRect.height > viewportHeight) {
        openTop();
      }

      // Prevent horizontal overflow
      if (left + popoverRect.width > viewportWidth) {
        left = viewportWidth - popoverRect.width - 8;
      }
      if (left < 8) left = 8;

      setCoords({ top, left });
    });
  }, [open, placement, offset]);

  // 🔁 Close when any item inside is clicked
  const handleItemClick = (e) => {
    const clickable = e.target.closest("button, a, [data-popover-item]");
    if (clickable) {
      setOpen(false);
    }
  };

  return (
    <>
      {/* Trigger */}
      <span
        ref={triggerRef}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((p) => !p);
        }}
        className="inline-flex cursor-pointer"
      >
        {trigger}
      </span>

      {/* Popover */}
      {open &&
        createPortal(
          <div
            ref={popoverRef}
            onClick={handleItemClick}
            style={{
              position: "fixed",
              top: coords.top,
              left: coords.left,
            }}
            className={`z-[9999] min-w-[160px] rounded-lg border border-slate-200 bg-white shadow-xl
              animate-in fade-in zoom-in-95 ${className}`}
          >
            {children}
          </div>,
          document.body
        )}
    </>
  );
}
